/**
 * The bottom row's money views (handoff 2026-10-24 §4.6; owner 2026-10-25: "it might be useful for planning or
 * reviewing", supplements "from the pay rules"). A day already worked shows what it cost (clocked hours, with
 * supplements) against what it took, the plan small under it; today and later show the plan against the forecast,
 * which can be typed in. The week: the target, the actual so far, the forecast (actual so far plus the plan for the
 * rest) and last week. Numbers from GET /admin/labour-cost?supplements=1 and the sales forecast.
 */
import type { CostDay } from '@/api/labour';
import { T } from '@/i18n';
import { num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import css from './MoneyRow.module.css';
import type { TMode } from './timeMode';

export type MoneyMode = 'pct' | 'cost';
type Sales = { actual: number | null; forecast: number | null; estimate: number | null };

const kr = (n: number) => `${Math.round(n).toLocaleString('nb-NO')} kr`;
const pct = (cost: number, sales: number | null) => (sales && sales > 0 ? (cost / sales) * 100 : null);
const p1 = (n: number) => `${num(Math.round(n * 10) / 10)} %`;

/**
 * A day's figures: what counts, and whether it is the worked or the planned side. With the Plan | Faktisk
 * switch (#201) every day is the plan, or every day is what was clocked (nothing clocked costs nothing).
 */
export function dayFigure(c: CostDay | undefined, s: Sales | undefined, past: boolean, mode?: TMode) {
  const sales = past ? (s?.actual ?? null) : (s?.forecast ?? s?.estimate ?? null);
  const worked = mode !== 'plan' && (past || mode === 'fakt') && !!c && c.actual.shifts > 0;
  const cost = c ? (worked ? c.actual.cost : mode === 'fakt' ? 0 : c.planned.cost) : 0;
  return {
    sales,
    cost,
    worked,
    planCost: c?.planned.cost ?? 0,
    share: pct(cost, sales),
    planShare: pct(c?.planned.cost ?? 0, sales),
  };
}

/** The week: actual so far over the worked days' sales, the forecast over the whole week's. */
export function weekFigures(
  days: string[],
  today: string,
  cost: Map<string, CostDay>,
  sales: Record<string, Sales>,
  mode?: TMode,
) {
  let doneCost = 0;
  let doneSales = 0;
  let allCost = 0;
  let allSales = 0;
  for (const d of days) {
    const f = dayFigure(cost.get(d), sales[d], d < today, mode);
    // a day without a sales figure has no share to give: it is left out of both sides, not counted as cost on nothing
    if (!f.sales) continue;
    // Faktisk: the week so far is the days that are over; today is still being clocked (#201)
    if (mode === 'fakt' && d >= today) continue;
    allCost += f.cost;
    allSales += f.sales;
    if (mode === 'fakt' || d < today) {
      doneCost += f.cost;
      doneSales += f.sales;
    }
  }
  return { actual: pct(doneCost, doneSales), forecast: pct(allCost, allSales), cost: allCost };
}

export function MoneyCells({
  tmode,
  mode,
  days,
  today,
  cost,
  sales,
  target,
  onSales,
  dayClass,
}: {
  tmode?: TMode | undefined;
  mode: MoneyMode;
  days: string[];
  today: string;
  cost: Map<string, CostDay>;
  sales: Record<string, Sales>;
  target: number;
  onSales: (day: string, value: number | null, forecast: boolean) => void;
  dayClass: (d: string) => string | undefined;
}) {
  return (
    <>
      {days.map((d) => {
        const past = d < today;
        if (tmode === 'fakt' && d > today)
          return (
            <div key={d} className={`${css.cell} ${dayClass(d) ?? ''}`} data-testid="money-day">
              <small className={css.sub} data-testid="not-yet">
                {T('Ikke ennå')}
              </small>
            </div>
          );
        const f = dayFigure(cost.get(d), sales[d], past, tmode);
        const over = f.share != null && f.share > target;
        const own = past ? (sales[d]?.actual ?? null) : (sales[d]?.forecast ?? null);
        return (
          <div key={d} className={`${css.cell} ${dayClass(d) ?? ''}`} data-testid="money-day">
            {mode === 'pct' ? (
              <>
                <b
                  className={`${css.big} ${over ? css.over : ''}`}
                  title={T('Lønn med tillegg, feriepenger, pensjon og arbeidsgiveravgift')}
                >
                  {f.share == null ? '–' : p1(f.share)}
                </b>
                {f.worked && f.planShare != null && (
                  <small className={css.sub}>{T('plan {p}', { p: p1(f.planShare) })}</small>
                )}
                <label className={css.sales}>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={css.in}
                    aria-label={`${past ? T('Omsetning') : T('Forventet omsetning')} ${Number(d.slice(8, 10))}.${Number(d.slice(5, 7))}`}
                    placeholder={
                      !past && sales[d]?.estimate != null
                        ? `≈ ${kr(sales[d]?.estimate ?? 0)}`
                        : T('Omsetning')
                    }
                    key={`${d}-${own ?? ''}`}
                    defaultValue={own == null ? '' : Math.round(own).toLocaleString('nb-NO')}
                    onBlur={(e) => {
                      const raw = e.target.value.replace(/[\s\u00a0kr.]/g, '').replace(',', '.');
                      const v = raw === '' ? null : Number(raw);
                      if (v != null && !Number.isFinite(v)) return;
                      if (v !== own) onSales(d, v, !past);
                    }}
                  />
                  {!past && <span>{T('prognose')}</span>}
                </label>
              </>
            ) : (
              <>
                <b className={css.big}>{kr(f.cost)}</b>
                <small className={css.sub}>
                  {f.worked
                    ? T('plan {k}', { k: kr(f.planCost) })
                    : tmode === 'plan'
                      ? T('planlagt')
                      : past || tmode === 'fakt'
                        ? T('ingen stemplet')
                        : T('planlagt')}
                </small>
              </>
            )}
          </div>
        );
      })}
    </>
  );
}

export function MoneyWeek({
  tmode,
  mode,
  days,
  today,
  cost,
  sales,
  target,
  lastWeek,
  onHugin,
}: {
  tmode?: TMode | undefined;
  mode: MoneyMode;
  days: string[];
  today: string;
  cost: Map<string, CostDay>;
  sales: Record<string, Sales>;
  target: number;
  lastWeek: number | null;
  onHugin?: (() => void) | null;
}) {
  const w = weekFigures(days, today, cost, sales, tmode);
  if (mode === 'cost')
    return (
      <div className={css.week}>
        <dl>
          <div>
            <dt>{tmode === 'fakt' ? T('Hittil') : T('Uka')}</dt>
            <dd>{kr(w.cost)}</dd>
          </div>
        </dl>
      </div>
    );
  return (
    <div className={css.week} data-testid="money-week">
      {/* one line under the picker; last week's figure on hover (owner 2026-10-27: the foot too tall) */}
      <dl
        className={css.line}
        title={lastWeek != null ? T('Forrige uke {p}', { p: p1(lastWeek) }) : undefined}
      >
        <div>
          <dt>{T('Mål')}</dt>
          <dd>{p1(target)}</dd>
        </div>
        {tmode !== 'plan' && (
          <div>
            <dt>{tmode === 'fakt' ? T('Hittil') : T('Faktisk hittil')}</dt>
            <dd className={w.actual != null && w.actual > target ? css.over : ''}>
              {w.actual == null ? '–' : p1(w.actual)}
            </dd>
          </div>
        )}
        {tmode !== 'fakt' && (
          <div>
            <dt>{T('Prognose')}</dt>
            <dd className={w.forecast != null && w.forecast > target ? css.over : ''}>
              {w.forecast == null ? '–' : p1(w.forecast)}
            </dd>
          </div>
        )}
      </dl>
      {onHugin && tmode !== 'fakt' && w.forecast != null && w.forecast > target && (
        <button type="button" className={css.hugin} onClick={onHugin}>
          <Icon name="sparkle" size={14} /> {T('Nå {p} med Hugin', { p: p1(target) })}
        </button>
      )}
    </div>
  );
}
